import { ACCOUNT_BOOK_ORDERS_ALLOWANCE_URL, ACCOUNT_BOOK_ORDERS_ORDER_URL } from "@icoach/router/routerPath";
import { isEqual } from "@util/utils";

/** 訂單列操作選單 — 非 UI、可序列化描述（供 mapper 轉成 OverflowMenu menuData） */
export const ACCOUNT_BOOK_ORDER_MENU_ACTION = {
	ALLOWANCE: "allowance",
	VOID_INVOICE: "voidInvoice",
	REISSUE_INVOICE: "reissueInvoice",
	DELETE_ORDER: "deleteOrder",
	RESTORE_ORDER: "restoreOrder",
	DOWNLOAD_ALLOWANCE_CERTIFICATE: "downloadAllowanceCertificate",
	UPLOAD_ALLOWANCE_CERTIFICATE: "uploadAllowanceCertificate",
	/** 已上傳折讓證明（confirmPath PDF 預覽） */
	PREVIEW_UPLOADED_ALLOWANCE_CERTIFICATE: "previewUploadedAllowanceCertificate",
};

/**
 * @typedef {{ type: 'link', text: string, href: string }} AccountBookOrderOverflowLinkItem
 * @typedef {{ type: 'action', text: string, action: string }} AccountBookOrderOverflowActionItem
 * @typedef {AccountBookOrderOverflowLinkItem | AccountBookOrderOverflowActionItem} AccountBookOrderOverflowMenuItem
 */

/**
 * 依後端列資料與選項，組出「要做哪些操作」的純設定（不含 React／路由元件）
 *
 * @param {object} rowData
 * @param {object} [options]
 * @param {boolean} [options.includeViewOrder=true]
 * @param {boolean} [options.includeAllowanceHistory=true]
 * @param {boolean} [options.includeRestoreOrder=true]
 * @param {boolean} [options.forceShowCertificateActions=false]
 * @param {boolean} [options.includeUploadCertificate=false]
 * @returns {AccountBookOrderOverflowMenuItem[]}
 */
export const getOrderMenuItems = (rowData, options = {}) => {
	const {
		includeViewOrder = true,
		includeAllowanceHistory = true,
		includeRestoreOrder = true,
		forceShowCertificateActions = false,
		includeUploadCertificate = false,
	} = options;
	const {
		status: orderStatus,
		canAllowance,
		canInvalid,
		canDelete,
		canReissuing,
		canRestore,
		canProcessCertificate,
		isUploadedCertificate,
		confirmPath,
		orderID,
	} = rowData || {};

	/** @type {AccountBookOrderOverflowMenuItem[]} */
	const items = [];

	const shouldShowViewOrder = includeViewOrder && orderID;
	if (shouldShowViewOrder) {
		items.push({ type: "link", text: "查看訂單", href: ACCOUNT_BOOK_ORDERS_ORDER_URL(orderID) });
	}

	if (canAllowance) {
		items.push({ type: "action", text: "訂單折讓", action: ACCOUNT_BOOK_ORDER_MENU_ACTION.ALLOWANCE });
	}

	if (canInvalid) {
		items.push({ type: "action", text: "作廢發票", action: ACCOUNT_BOOK_ORDER_MENU_ACTION.VOID_INVOICE });
	}

	if (canReissuing) {
		items.push({ type: "action", text: "重開發票", action: ACCOUNT_BOOK_ORDER_MENU_ACTION.REISSUE_INVOICE });
	}

	if (canDelete) {
		items.push({ type: "action", text: "刪除訂單", action: ACCOUNT_BOOK_ORDER_MENU_ACTION.DELETE_ORDER });
	}

	const shouldShowRestoreOrder = includeRestoreOrder && (canRestore || isEqual(orderStatus, 8));
	if (shouldShowRestoreOrder) {
		items.push({ type: "action", text: "恢復訂單", action: ACCOUNT_BOOK_ORDER_MENU_ACTION.RESTORE_ORDER });
	}

	const canShowCertificateActions = canProcessCertificate || forceShowCertificateActions;
	const canShowUploadCertificate = canProcessCertificate || includeUploadCertificate;
	if (canShowCertificateActions) {
		items.push({
			type: "action",
			text: "下載三聯折讓證明單",
			action: ACCOUNT_BOOK_ORDER_MENU_ACTION.DOWNLOAD_ALLOWANCE_CERTIFICATE,
		});
		if (canShowUploadCertificate) {
			items.push({
				type: "action",
				text: "上傳三聯折讓證明單",
				action: ACCOUNT_BOOK_ORDER_MENU_ACTION.UPLOAD_ALLOWANCE_CERTIFICATE,
			});
		}
		if (isUploadedCertificate && confirmPath) {
			items.push({
				type: "action",
				text: "預覽三聯折讓證明單",
				action: ACCOUNT_BOOK_ORDER_MENU_ACTION.PREVIEW_UPLOADED_ALLOWANCE_CERTIFICATE,
			});
		}
	}

	if (includeAllowanceHistory && orderID) {
		items.push({ type: "link", text: "折讓歷史", href: ACCOUNT_BOOK_ORDERS_ALLOWANCE_URL(orderID) });
	}

	return items;
};
